import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ClientLayout from '../components/layouts/ClientLayout';
import AdminLayout from '../admin/layouts/AdminLayout';
import AdminThemeProvider from '../admin/theme/AdminThemeProvider';
import RequireAdmin from './RequireAdmin';
import RedirectIfAdmin from './RedirectIfAdmin';
import RequireCustomer from './RequireCustomer';
import PageLoadingFallback from './PageLoadingFallback';
import { CUSTOMER_MEMBERSHIP_ENABLED } from '../config/customerFeatures';

const Home = lazy(() => import('../client/pages/Home'));
const About = lazy(() => import('../client/pages/About'));
const Contact = lazy(() => import('../client/pages/Contact'));
const Gallery = lazy(() => import('../client/pages/Gallery'));
const Articles = lazy(() => import('../client/pages/Articles'));
const ArticleDetail = lazy(() => import('../client/pages/Articles/ArticleDetail'));
const Booking = lazy(() => import('../client/pages/Booking'));
const Login = lazy(() => import('../client/pages/Login'));
const Register = lazy(() => import('../client/pages/Register'));
const VerifyEmail = lazy(() => import('../client/pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('../client/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('../client/pages/ResetPassword'));
const MyBookings = lazy(() => import('../client/pages/MyBookings'));
const Membership = lazy(() => import('../client/pages/Membership'));
const Packages = lazy(() => import('../client/pages/Packages'));
const MyAccount = lazy(() => import('../client/pages/MyAccount'));
const MembershipPaymentPage = lazy(() => import('../client/pages/MembershipPayment'));
const PackagePaymentPage = lazy(() => import('../client/pages/PackagePayment'));
const MembershipPaymentReturnPage = lazy(() => import('../client/pages/MembershipPaymentReturn'));
const PackagePaymentReturnPage = lazy(() => import('../client/pages/PackagePaymentReturn'));
const MembershipPaymentSimulatePage = lazy(() => import('../client/pages/MembershipPaymentSimulate'));
const PackagePaymentSimulatePage = lazy(() => import('../client/pages/PackagePaymentSimulate'));
const PaymentPage = lazy(() => import('../client/pages/Payment'));
const PaymentReturnPage = lazy(() => import('../client/pages/PaymentReturn'));
const PaymentSimulatePage = lazy(() => import('../client/pages/PaymentSimulate'));
const Terms = lazy(() => import('../client/pages/Terms'));
const Privacy = lazy(() => import('../client/pages/Privacy'));

const AdminLogin = lazy(() => import('../admin/pages/Login'));
const AdminForgotPassword = lazy(() => import('../admin/pages/ForgotPassword'));
const AdminResetPassword = lazy(() => import('../admin/pages/ResetPassword'));
const AdminDashboard = lazy(() => import('../admin/pages/Dashboard'));
const AdminNotifications = lazy(() => import('../admin/pages/Notifications'));
const AdminBookings = lazy(() => import('../admin/pages/Bookings'));
const LegacyAddBookingRedirect = lazy(() => import('../admin/pages/Bookings/LegacyAddBookingRedirect'));
const AdminPayments = lazy(() => import('../admin/pages/Payments'));
const AdminReports = lazy(() => import('../admin/pages/Reports'));
const AdminCourtSettings = lazy(() => import('../admin/pages/CourtSettings'));
const AdminMembershipSettings = lazy(() => import('../admin/pages/MembershipSettings'));
const AdminPackageSettings = lazy(() => import('../admin/pages/PackageSettings'));
const AdminUsers = lazy(() => import('../admin/pages/Users'));
const AdminVenueShowcase = lazy(() => import('../admin/pages/VenueShowcase'));
const AdminGallery = lazy(() => import('../admin/pages/Gallery'));
const AdminBlogs = lazy(() => import('../admin/pages/Blogs'));

export default function AppRoutes() {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        <Route element={<ClientLayout />}>
          <Route index element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="gallery" element={<Gallery />} />
          <Route path="articles" element={<Articles />} />
          <Route path="articles/:slug" element={<ArticleDetail />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="booking" element={<Booking />} />
          <Route
            path="membership"
            element={
              CUSTOMER_MEMBERSHIP_ENABLED ? <Membership /> : <Navigate to="/packages" replace />
            }
          />
          <Route path="packages" element={<Packages />} />
          <Route
            path="package/pay/:id"
            element={
              <RequireCustomer>
                <PackagePaymentPage />
              </RequireCustomer>
            }
          />
          <Route path="package/payment/return" element={<PackagePaymentReturnPage />} />
          <Route path="package/payment/simulate" element={<PackagePaymentSimulatePage />} />
          <Route
            path="my-account"
            element={
              <RequireCustomer>
                <MyAccount />
              </RequireCustomer>
            }
          />
          <Route
            path="membership/pay/:id"
            element={
              CUSTOMER_MEMBERSHIP_ENABLED ? (
                <RequireCustomer>
                  <MembershipPaymentPage />
                </RequireCustomer>
              ) : (
                <Navigate to="/packages" replace />
              )
            }
          />
          <Route
            path="membership/payment/return"
            element={
              CUSTOMER_MEMBERSHIP_ENABLED ? (
                <MembershipPaymentReturnPage />
              ) : (
                <Navigate to="/packages" replace />
              )
            }
          />
          <Route
            path="membership/payment/simulate"
            element={
              CUSTOMER_MEMBERSHIP_ENABLED ? (
                <MembershipPaymentSimulatePage />
              ) : (
                <Navigate to="/packages" replace />
              )
            }
          />
          <Route path="booking/pay/:id" element={<PaymentPage />} />
          <Route path="booking/payment/return" element={<PaymentReturnPage />} />
          <Route path="booking/payment/simulate" element={<PaymentSimulatePage />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="verify-email" element={<VerifyEmail />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route
            path="my-bookings"
            element={
              <RequireCustomer>
                <MyBookings />
              </RequireCustomer>
            }
          />
        </Route>

        <Route
          path="/admin/login"
          element={
            <AdminThemeProvider>
              <RedirectIfAdmin>
                <AdminLogin />
              </RedirectIfAdmin>
            </AdminThemeProvider>
          }
        />
        <Route
          path="/admin/forgot-password"
          element={
            <AdminThemeProvider>
              <AdminForgotPassword />
            </AdminThemeProvider>
          }
        />
        <Route
          path="/admin/reset-password"
          element={
            <AdminThemeProvider>
              <AdminResetPassword />
            </AdminThemeProvider>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminThemeProvider>
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            </AdminThemeProvider>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="bookings/add" element={<LegacyAddBookingRedirect />} />
          <Route path="schedule" element={<Navigate to="/admin/bookings?view=calendar" replace />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="court-settings" element={<AdminCourtSettings />} />
          <Route path="membership-settings" element={<AdminMembershipSettings />} />
          <Route path="package-settings" element={<AdminPackageSettings />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="venue-showcase" element={<AdminVenueShowcase />} />
          <Route path="gallery" element={<AdminGallery />} />
          <Route path="blogs" element={<AdminBlogs />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
